/**
 * Build the RTM Services view from Prometheus + Loki (ClickUp 86e1w8ude, phase 1).
 *
 * - `fetchRtmEnvelope()` assembles a `ServicesEnvelope` (services table +
 *   hosting roll-up) from a handful of instant PromQL queries, joined per pod.
 * - `fetchServiceLogs()` pulls recent log lines for one pod from Loki.
 *
 * All PromQL/LogQL below was validated against monitor.stage.airmarket.io.
 * One pod == one row in the services table; namespaces in
 * `config.grafana.rtmNamespaces` define which pods count as "RTM services"
 * (narrow that list for per-tenant filtering).
 */
import { config } from '@/lib/config';
import {
  type RtmHosting,
  type RtmPodMetric,
  type RtmServiceMetric,
  ServicesEnvelope,
} from '@/lib/mqtt/schemas/services';
import {
  type PromMatrix,
  type PromSample,
  lokiRange,
  promInstantAll,
  promRange,
  promRangeAll,
} from './client';

/** OR-regex over the configured RTM namespaces, RE2-escaped. */
function nsRegex(): string {
  return config.grafana.rtmNamespaces
    .map((ns) => ns.replace(/[.*+?()|[\]{}\\^$]/g, '\\$&'))
    .join('|');
}

const podKey = (s: PromSample) => `${s.metric.namespace ?? ''}/${s.metric.pod ?? ''}`;

/** Vector → Map keyed by namespace/pod, value = parsed float. */
function byPod(samples: PromSample[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const s of samples) m.set(podKey(s), Number(s.value[1]));
  return m;
}

function scalar(samples: PromSample[], fallback = 0): number {
  const v = samples[0]?.value[1];
  const n = v != null ? Number(v) : Number.NaN;
  return Number.isFinite(n) ? n : fallback;
}

type PodPhase = { namespace: string; pod: string; phase: string };

export async function fetchRtmEnvelope(): Promise<ServicesEnvelope> {
  const ns = nsRegex();
  const sel = `namespace=~"${ns}"`;
  const cont = `${sel},container!=""`;

  // Per-pod metrics — one instant query each, run in parallel.
  // Defaults guard against `noUncheckedIndexedAccess` (tuple slots are T|undefined).
  const [
    phaseS = [],
    restartsS = [],
    rxS = [],
    txS = [],
    memS = [],
    cpuS = [],
    memLimitS = [],
    uptimeS = [],
  ] = await promInstantAll([
    `kube_pod_status_phase{${sel},phase!=""} == 1`,
    `sum by (namespace,pod)(kube_pod_container_status_restarts_total{${sel}})`,
    `sum by (namespace,pod)(rate(container_network_receive_bytes_total{${sel}}[5m]))/1048576`,
    `sum by (namespace,pod)(rate(container_network_transmit_bytes_total{${sel}}[5m]))/1048576`,
    `sum by (namespace,pod)(container_memory_working_set_bytes{${cont}})/1048576`,
    `sum by (namespace,pod)(rate(container_cpu_usage_seconds_total{${cont}}[5m]))`,
    `sum by (namespace,pod)(kube_pod_container_resource_limits{resource="memory",${sel}})/1048576`,
    `max by (namespace,pod)(avg_over_time(kube_pod_status_ready{condition="true",${sel}}[6h]))*100`,
  ]);

  const restarts = byPod(restartsS);
  const rx = byPod(rxS);
  const tx = byPod(txS);
  const memMiB = byPod(memS);
  const cpuCores = byPod(cpuS);
  const memLimitMiB = byPod(memLimitS);
  const uptime = byPod(uptimeS);

  const pods: PodPhase[] = phaseS.map((s) => ({
    namespace: s.metric.namespace ?? '',
    pod: s.metric.pod ?? '',
    phase: s.metric.phase ?? '',
  }));

  const services: RtmServiceMetric[] = pods
    .map((p): RtmServiceMetric => {
      const key = `${p.namespace}/${p.pod}`;
      const pod_status =
        p.phase === 'Running' ? 'running' : p.phase === 'Pending' ? 'degraded' : 'down';
      const rxv = rx.get(key);
      const txv = tx.get(key);
      return {
        service_name: p.pod,
        namespace: p.namespace,
        pod_status,
        // Readiness over the last 6h; default to 100 for a running pod whose
        // readiness series hasn't been scraped yet (avoids a phantom 0%).
        uptime_pct: round(uptime.get(key) ?? (pod_status === 'running' ? 100 : 0), 1, 0, 100),
        rx: rxv != null ? round(rxv, 2) : null,
        tx: txv != null ? round(txv, 2) : null,
        restarts: Math.round(restarts.get(key) ?? 0),
        // Not generically derivable from cluster metrics — app-specific
        // throughput lands in phase 2 once the BE exposes it. UI shows '—'.
        msg_in: null,
        msg_out: null,
        active: null,
      };
    })
    .sort((a, b) => a.service_name.localeCompare(b.service_name));

  // Hosting roll-up — scoped to the same RTM namespaces so it stays
  // consistent with the services table (and with per-tenant filtering).
  const [upS = [], downS = [], memUsedS = [], nsCpuS = [], allocCpuS = []] = await promInstantAll([
    `count(kube_pod_status_phase{${sel},phase="Running"} == 1) or vector(0)`,
    `count(kube_pod_status_phase{${sel},phase=~"Failed|Unknown|Pending"} == 1) or vector(0)`,
    `sum(container_memory_working_set_bytes{${cont}})/1073741824`,
    `sum(rate(container_cpu_usage_seconds_total{${cont}}[5m]))`,
    `sum(kube_node_status_allocatable{resource="cpu"})`,
  ]);

  const pods_down = Math.round(scalar(downS));
  const allocCpu = scalar(allocCpuS, 1);
  const cpu_pct = round(allocCpu > 0 ? (100 * scalar(nsCpuS)) / allocCpu : 0, 1, 0, 100);
  const health: RtmHosting['health'] =
    pods_down === 0 ? 'Healthy' : pods_down <= 2 ? 'Degraded' : 'Unhealthy';

  // "Running Pods" list — top 8 by working-set memory. memPct/cpuPct are
  // utilisation vs the pod's limit when one is set, else relative to the
  // busiest pod in view (clearly a comparative bar, never a fake absolute).
  const maxMem = Math.max(1, ...memMiB.values());
  const maxCpu = Math.max(0.001, ...cpuCores.values());
  const podList: RtmPodMetric[] = [...memMiB.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([key, mem]) => {
      const limit = memLimitMiB.get(key);
      const memPct = limit && limit > 0 ? (100 * mem) / limit : (100 * mem) / maxMem;
      const cpu = cpuCores.get(key) ?? 0;
      return {
        name: key.split('/')[1] ?? key,
        memPct: round(memPct, 0, 0, 100),
        cpuPct: round((100 * cpu) / maxCpu, 0, 0, 100),
        memMiB: round(mem, 0),
      };
    });

  const hosting: RtmHosting = {
    pods_up: Math.round(scalar(upS)),
    pods_down,
    memory_gib: round(scalar(memUsedS), 1),
    cpu_pct,
    health,
    pods: podList,
  };

  return ServicesEnvelope.parse({
    services,
    hosting,
    cloud_processing: null,
    received_at: new Date().toISOString(),
  });
}

/* ── Loki service logs ──────────────────────────────────────────────────── */

export type ServiceLogLine = { ts: number; level: 'info' | 'warn' | 'crit'; msg: string };

const WINDOW_MS: Record<string, number> = {
  '1m': 60_000,
  '5m': 5 * 60_000,
  '15m': 15 * 60_000,
  '60m': 60 * 60_000,
  '24h': 24 * 60 * 60_000,
};

function normLevel(raw: string | undefined, line: string): ServiceLogLine['level'] {
  const v = (raw ?? '').toLowerCase();
  if (v.includes('err') || v.includes('crit') || v.includes('fatal')) return 'crit';
  if (v.includes('warn')) return 'warn';
  // Treat absent / 'unknown' / 'none' as no level info → infer from the line.
  if (!v || v === 'unknown' || v === 'none') {
    const l = line.toLowerCase();
    if (/\b(error|fatal|panic)\b/.test(l)) return 'crit';
    if (/\bwarn(ing)?\b/.test(l)) return 'warn';
  }
  return 'info';
}

/**
 * Recent log lines for one pod, newest first. The services table's
 * `service_name` is the Prometheus `pod` label, which equals Loki's
 * `instance` label (verified: e.g. `dss-cockroachdb-0`, `emqx-0`,
 * `blender-deployment-…`). Fluent-bit does NOT set a `pod` label on these
 * streams, so we filter by `instance`.
 */
export async function fetchServiceLogs(
  pod: string,
  windowKey: string,
  limit = 100,
): Promise<ServiceLogLine[]> {
  const span = WINDOW_MS[windowKey] ?? WINDOW_MS['5m']!;
  const end = Date.now();
  const start = end - span;
  const escaped = pod.replace(/["\\]/g, '\\$&');
  const streams = await lokiRange(`{instance="${escaped}"}`, start, end, limit, 'backward');

  const lines: ServiceLogLine[] = [];
  for (const s of streams) {
    const level = s.stream.level ?? s.stream.detected_level;
    for (const [tsNs, raw] of s.values) {
      lines.push({
        ts: Number(BigInt(tsNs) / 1_000_000n),
        level: normLevel(level, raw),
        msg: raw,
      });
    }
  }
  lines.sort((a, b) => b.ts - a.ts);
  return lines.slice(0, limit);
}

/* ── Time-series (range) data for the charts ────────────────────────────── */

export type ChartWindow = '1m' | '5m' | '15m' | '60m' | '24h';

/** Per-window range span + step (resolution) + rate() lookback. ~30–48 points. */
const WINDOW_RANGE: Record<ChartWindow, { spanMs: number; stepS: number; rateS: number }> = {
  '1m': { spanMs: 60_000, stepS: 2, rateS: 60 },
  '5m': { spanMs: 5 * 60_000, stepS: 10, rateS: 60 },
  '15m': { spanMs: 15 * 60_000, stepS: 30, rateS: 120 },
  '60m': { spanMs: 60 * 60_000, stepS: 120, rateS: 300 },
  '24h': { spanMs: 24 * 60 * 60_000, stepS: 1800, rateS: 1800 },
};

function win(windowKey: string) {
  return WINDOW_RANGE[
    (windowKey as ChartWindow) in WINDOW_RANGE ? (windowKey as ChartWindow) : '5m'
  ]!;
}

/** First series' y-values as numbers (single-series range queries). */
function seriesOf(m: PromMatrix[]): number[] {
  return (m[0]?.values ?? []).map(([, v]) => Number(v)).filter((n) => Number.isFinite(n));
}

export type ServiceSeries = {
  /** Network throughput MiB/s. */
  netRx: number[];
  netTx: number[];
  /** Message rate msg/min — null = metric not mapped (app-specific). */
  msgIn: number[] | null;
  msgOut: number[] | null;
  /** Active items — null = metric not mapped. */
  active: number[] | null;
};

/**
 * Time-series for the Service Graphing detail charts of one pod. Only network
 * throughput is derivable from cluster metrics today; message-rate / active
 * are app-specific and stay null (chart shows a "not mapped" placeholder).
 */
export async function fetchServiceSeries(pod: string, windowKey: string): Promise<ServiceSeries> {
  const { spanMs, stepS, rateS } = win(windowKey);
  const end = Date.now();
  const start = end - spanMs;
  const ns = nsRegex();
  const p = pod.replace(/[\\"]/g, '\\$&');
  const sel = `namespace=~"${ns}",pod="${p}"`;

  const [rx, tx] = await promRangeAll(
    [
      `sum(rate(container_network_receive_bytes_total{${sel}}[${rateS}s]))/1048576`,
      `sum(rate(container_network_transmit_bytes_total{${sel}}[${rateS}s]))/1048576`,
    ],
    start,
    end,
    stepS,
  );

  return {
    netRx: seriesOf(rx ?? []),
    netTx: seriesOf(tx ?? []),
    msgIn: null,
    msgOut: null,
    active: null,
  };
}

export type HostingSeries = {
  memGiB: number[];
  cpuMilli: number[];
  netRx: number[];
  netTx: number[];
  uptimePct: number[];
  /** Per-pod working-set memory (MiB) time-series, keyed by pod name. */
  podMem: Map<string, number[]>;
};

/** Time-series for the Hosting Environment mini charts + per-pod sparklines. */
export async function fetchHostingSeries(windowKey: string): Promise<HostingSeries> {
  const { spanMs, stepS, rateS } = win(windowKey);
  const end = Date.now();
  const start = end - spanMs;
  const ns = nsRegex();
  const sel = `namespace=~"${ns}"`;
  const cont = `${sel},container!=""`;

  const [mem, cpu, rx, tx, uptime] = await promRangeAll(
    [
      `sum(container_memory_working_set_bytes{${cont}})/1073741824`,
      `sum(rate(container_cpu_usage_seconds_total{${cont}}[${rateS}s]))*1000`,
      `sum(rate(container_network_receive_bytes_total{${sel}}[${rateS}s]))/1048576`,
      `sum(rate(container_network_transmit_bytes_total{${sel}}[${rateS}s]))/1048576`,
      `avg(kube_pod_status_ready{condition="true",${sel}})*100`,
    ],
    start,
    end,
    stepS,
  );

  // Per-pod memory in one query → matrix; key by the `pod` label.
  const podMatrix = await promRange(
    `sum by (pod)(container_memory_working_set_bytes{${cont}})/1048576`,
    start,
    end,
    stepS,
  );
  const podMem = new Map<string, number[]>();
  for (const s of podMatrix) {
    const name = s.metric.pod;
    if (name)
      podMem.set(
        name,
        s.values.map(([, v]) => Number(v)),
      );
  }

  return {
    memGiB: seriesOf(mem ?? []),
    cpuMilli: seriesOf(cpu ?? []),
    netRx: seriesOf(rx ?? []),
    netTx: seriesOf(tx ?? []),
    uptimePct: seriesOf(uptime ?? []),
    podMem,
  };
}

/** Clamp + round to `dp` decimal places. */
function round(
  n: number,
  dp = 0,
  min = Number.NEGATIVE_INFINITY,
  max = Number.POSITIVE_INFINITY,
): number {
  if (!Number.isFinite(n)) return min === Number.NEGATIVE_INFINITY ? 0 : min;
  const f = 10 ** dp;
  return Math.min(max, Math.max(min, Math.round(n * f) / f));
}
